import { BaseFloor } from "./dungeon/floors/BaseFloor.js";
import { EnemyPool } from "../entities/EnemyPool.js";
import { Vector3 } from "@babylonjs/core";

/**
 * @class EntityManager
 * @description Gère le cycle de vie de tous les ennemis présents en jeu, le pooling et les collisions avec le joueur.
 */
export class EntityManager {
    /**
     * @param {import("../core/GameManager.js").GameContext} context 
     */
    constructor(context) {
        /** @type {import("../core/GameManager.js").GameContext} */
        this.context = context;
        /** @type {import("@babylonjs/core").Scene} */
        this.scene = context.scene;
        /** @type {import("../entities/Enemy.js").Enemy[]} */
        this.enemies = [];
        /** @type {EnemyPool} */
        this.pool = new EnemyPool(this.scene);
    }

    /**
     * Fait apparaître un ennemi d'un type spécifique à une position donnée.
     * @param {string} type - Type d'ennemi (Traqueur, Sentinelle, etc.)
     * @param {Vector3} position 
     */
    spawnEnemy(type, position) {
        if (type === "Mix") {
            const types = ["Traqueur", "Sentinelle", "Pulse", "Bombardier"];
            return this.spawnEnemy(types[Math.floor(Math.random() * types.length)], position);
        }

        const enemy = this.pool.get(type, position);

        if (this.context.levelManager) {
            const floorNumber = this.context.levelManager.currentFloor ?? 1;

            const hpMult = BaseFloor.getHpMult(floorNumber);
            if (hpMult > 1.0 && typeof enemy.applyHpMult === 'function') {
                enemy.applyHpMult(hpMult);
            }

            if (typeof enemy.applyFloorScaling === 'function') {
                enemy.applyFloorScaling(floorNumber);
            }

            if ('arenaThreshold' in enemy) {
                const room = this.context.levelManager.currentRoom;
                const threshold = room && room.arenaThreshold ? room.arenaThreshold : null;
                enemy.arenaThreshold = threshold ? { ...threshold } : null;
            }
        }

        this.enemies.push(enemy);
    }

    /**
     * Mise à jour logique de tous les ennemis et détection des collisions.
     * @param {import("../entities/Player.js").Player} player 
     * @param {import("./ai/DataCollector.js").DataCollector} aiCollector 
     * @returns {boolean} True si le joueur a été touché par un ennemi ce tour-ci
     */
    update(player, aiCollector) {
        let playerHit = false;

        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const enemy = this.enemies[i];

            if (enemy.isDestroyed) {
                this.enemies.splice(i, 1);
                continue;
            }

            enemy.think(player, this, aiCollector);

            if (player.mesh && enemy.mesh) {
                // Optimisation Large-Phase : Test de distance au carré avant intersection complexe
                const distSq = Vector3.DistanceSquared(player.mesh.position, enemy.mesh.position);
                
                // Rayon combiné approximatif (Player ~0.5 + Enemy ~0.6) = 1.1 -> distSq ~ 1.21
                // On prend 2.25 (1.5 * 1.5) pour être large et éviter les faux négatifs
                if (distSq < 2.25) {
                    if (player.mesh.intersectsMesh(enemy.mesh, false)) {
                        if (player.isDashing) {
                            if (!player.canHitEnemy(enemy)) continue;

                            const dropPosition = enemy.mesh.position.clone();
                            const isDead = enemy.takeDamage(player.attackPower || 1);

                            if (isDead) {
                                if (aiCollector) aiCollector.recordKill(true);
                                if (this.context.vfx) this.context.vfx.enemyDeath(dropPosition, enemy.type);
                                if (this.context.levelManager) {
                                    if (Math.random() < 0.25) this.context.levelManager.spawnBonusDrop(dropPosition, enemy.type);
                                    if (enemy.type === "NEXUS") this.context.levelManager.onBossDefeated();
                                }
                            } else {
                                if (this.context.audioManager) this.context.audioManager.playSound("hit");
                                if (this.context.vfx) this.context.vfx.dashImpact(dropPosition);
                            }
                        } else {
                            playerHit = true;
                        }
                    }
                }
            }
        }

        return playerHit;
    }

    /**
     * Supprime tous les ennemis actifs (retour au pool).
     */
    clearAll() {
        this.enemies.forEach(enemy => enemy.deactivate());
        this.enemies = [];
    }

    /**
     * Supprime tous les ennemis sauf le Boss (NEXUS).
     */
    clearMinions() {
        for (let i = this.enemies.length - 1; i >= 0; i--) {
            const enemy = this.enemies[i];
            if (enemy.type !== "NEXUS") {
                enemy.deactivate();
                this.enemies.splice(i, 1);
            }
        }
    }

    /**
     * Applique un boost d'agressivité aux ennemis existants selon le profil IA.
     * @param {import("../logic/ai/DataCollector.js").DataCollector} ai
     */
    applyAdaptation(ai) {
        const evasion    = ai.getEvasionLevel();   // 0 = agressif, 1 = esquiveur
        const aggression = ai.getAggressionLevel(); // 0 = passif,   1 = hyper-agressif

        this.enemies.forEach(enemy => {
            if (enemy.isDestroyed) return;

            if (enemy.type === "Traqueur") {
                // Plus le joueur esquive, plus le Traqueur accélère (plafonné à 0.30)
                const targetSpeed = enemy.speed + evasion * 0.06;
                enemy.speed = Math.min(targetSpeed, 0.30);

            } else if (enemy.type === "Sentinelle" || enemy.type === "SentinelleElite") {
                // Plus le joueur est agressif, plus la Sentinelle se rapproche pour tirer
                if (aggression > 0.5) enemy.attackRange = Math.max((enemy.attackRange ?? 12) - 2, 6);

            } else if (enemy.type === "Bombardier") {
                // Raccourcit la portée de fuite pour rester plus menaçant
                if (evasion > 0.4) enemy.speed = Math.min(enemy.speed + 0.02, 0.14);
            }
        });
    }

    /**
     * Nettoyage définitif des ressources.
     */
    dispose() {
        this.pool.disposeAll();
        this.enemies = [];
    }

    /** @returns {number} */
    getEnemyCount()        { return this.enemies.length; }
    
    /** @returns {import("../entities/Enemy.js").Enemy[]} */
    getEnemiesByType(type) { return this.enemies.filter(e => e.type === type && !e.isDestroyed); }
    
    /** @returns {boolean} */
    isBossAlive()          { return this.enemies.some(e => e.type === "NEXUS" && !e.isDestroyed); }
}